/**
 * 작성자 : 홍선영
 * 날짜 : 2024.10.28
 * 기능 : 사내 사용자 정보
 * 경로 : 사원 관리 > 사원 정보 > 사원 정보 (src/pages/c_user/user1/userInfo)
 */

import React, { useEffect, Dispatch, SetStateAction, useState } from 'react';
import { toast } from 'react-toastify';
import { userState } from '../../atoms';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { COMCD_LOCK_YN, COMCD_USE_YN, USER_GRANT, WORK_STATUS_SEARCH } from '../../_constants';
import { BtnBlue, BtnGray, BtnGreen, BtnLightBlue, BtnRed } from '../Button';
import Input from '../Input';
import IpInput from '../IpInput';
import NumbersInput from '../NumbersInput';
import SelectBox from '../SelectBox';
import { getAnnualDayString, getAnnualHourString } from '../../utils/getAnnualnum';
import { useSetAuth } from '../../utils/useSetAuth';
import { applyBorderStyle } from '../../utils/applyBorderStyle';
import { checkIdDuplicate } from '../../utils/checkIdDuplicate';
import { registerIp } from '../../services/register';
import { useTranslation } from 'react-i18next';
import { ISelectBox } from 'customTypes';
import DatePickerComponent from '../DatePicker';
import { formatDate, isOverOneYear } from '../../utils/formatDate';
import { AddressSearch } from '../AddressSearch';
import LogoFileInput from '../LogoFileInput';
import ClickableContainer from '../../pages/h_cctv/RealtimeCCTV/ClickableContainer';
import TableLamp from '../TableLamp';
import { setupFlutterFileHandlers } from '../../utils/flutterBridge';

type UpdatedLogoState = 'N' | 'D' | 'U';

const WORK_STATUS_SEARCH_WITHOUT_ALL = WORK_STATUS_SEARCH.filter((el) => el.wWorkstatus !== 'A');

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

const Root = styled.div<IUserInputFormStyle>`
  padding: 0.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100% - 57px);
  overflow: auto;
  justify-content: flex-start;
  ul > li {
    width: 100% !important;
    max-width: none !important;
  }

  .inputForm {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem;
    flex-grow: 1;
    overflow: auto;
    button {
      height: 2.5rem;
      font-size: 0.75rem;
      flex-shrink: 0;
      padding: 0 0.75rem;
      border-radius: 0.25rem;
    }
    button.gray {
      font-weight: 500;
      color: var(--zinc-200);
    }
    label {
      width: 4.625rem;
      min-width: fit-content;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 0.875rem;
      white-space: break-spaces;
    }
    .inputForm-group-1280 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1280px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem 2rem;
      }
    }
    .inputForm-group-1536 {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      @media (min-width: 1536px) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem 2rem;
      }
    }
    .inputForm-group-1536.withBtn {
      flex-direction: row;
      gap: 0.5rem;
      button {
        transition: none;
      }
      @media (min-width: 1536px) {
        gap: 1rem 2rem;
        button {
          margin-left: 0;
          width: fit-content;
        }
      }
    }
    .inputForm-group-1536.withBtn.inputColumnFour {
      input {
        padding: 0;
      }
    }
    .inputForm-row.labelInInput {
      display: flex;
      gap: 0.5rem;
      flex-grow: 1;
      > div {
        flex-grow: 1;
      }
    }
    .inputForm-row.labelOutInput,
    .inputForm-row.labelInInput > div > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.5rem;
      input {
        flex-shrink: 1;
        height: 2.5rem;
        font-size: 0.875rem;
        /* padding: 0; */
        padding: 0 0.5rem;
        &:disabled {
          background-color: var(--tonal);
          color: var(--text-disabled);
        }
      }
      > .viewOnly {
        height: 2.5rem;
        font-size: 0.875rem;
        background-color: rgba(0, 0, 0, 0.05);
        color: var(--text-primary);
        border-radius: 0.25rem;
        padding: 0 0.75rem;
        display: flex;
        align-items: center;
      }
      > div {
        flex-grow: 1;
        > div > div > ul {
          height: 2.5rem;
          li {
            display: flex;
            span {
              flex-grow: 1;
              width: 4rem;
            }
          }
        }
      }
    }
    .detailInfo-group {
      margin: 1rem 0;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      @media (min-width: 1536px) {
        grid-template-columns: repeat(3, 1fr);
      }
      .flex-col.detailInfo {
        flex-grow: 1;
        width: 100%;
        div {
          height: 2.5rem;
          color: var(--text-tertiary);
          background-color: var(--tonal);
          border-radius: 0.25rem;
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          font-size: 0.875rem;
        }
      }
    }
  }
  input {
    :disabled {
      background-color: #eeeeee;
    }
  }

  .col-span-img {
    grid-row: span 5;
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 17.5rem;
    border-radius: 0.25rem;
    gap: 0.5rem;
    overflow: hidden;
    user-select: none;
    .titleName {
      width: 5.5rem;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 0.875rem;
      color: var(--text-secondary);
    }
    .logo-input-container {
      flex-grow: 1;
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      padding: 0.5rem;
      background-color: var(--tonal);
      justify-content: space-around;
      align-items: center;
      .description {
        font-size: 0.875rem;
        color: var(--filled-amber-deep);
        display: flex;
        align-items: center;
        gap: 0.25rem;
        .material-symbols-rounded {
          color: var(--filled-orange);
          font-size: 1.25rem;
        }
      }
    }
    > div {
      display: flex;
      gap: 0.25rem;
    }

    @media screen and (max-width: 1024px) {
      min-height: 14rem;
    }
  }
  .userImageWrapper {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-height: 12rem;
    max-width: 18rem;
    justify-content: flex-end;
    overflow: hidden;
    color: var(--text-secondary);
    label {
      width: fit-content;
      margin: 1rem 0 0 0;
      > div {
        line-height: 2;
        padding: 0 1rem;
        border-radius: 5px;
      }
    }

    img {
      max-height: 9rem;
    }

    .smallBtn {
      cursor: pointer;
      color: #eaeaea;
      background-color: #8950fc;
      border: 1px solid #8950fc;
      &:hover {
        color: #eaeaea;
        background-color: rgba(137, 80, 252, 0.6);
      }
    }
  }
  @media screen and (max-width: 1024px) {
  }
  @media screen and (max-width: 767px) {
    width: 100%;
    height: fit-content;
    overflow: auto;
  }
`;
interface IUserInputFormStyle {
  width: string;
  flexDirection?: string;
}

interface IUserInputForm {
  style: IUserInputFormStyle;
  state: any;
  setState: Dispatch<SetStateAction<any>>;
  isNewAdd?: boolean;
  userInfoInputFormRef: React.RefObject<any>;
  onClickNewAdd: () => void;
  onClickSave: () => void;
  onClickApproval: () => void;
  onClickDelete: () => void;
  onClickInitPwd: () => void;
  telNumState: any;
  setTelNumState: Dispatch<SetStateAction<any>>;
  hpNumState: any;
  setHpNumState: Dispatch<SetStateAction<any>>;
  ipInfoState: any;
  setIpInfoState: Dispatch<SetStateAction<any>>;
  emerNumState: any;
  setEmerNumState: Dispatch<SetStateAction<any>>;
  gCd: any;
  setGCd: Dispatch<SetStateAction<any>>;
  lockYn: any;
  useYn: any;
  setUseYn: Dispatch<SetStateAction<any>>;
  setLockYn: Dispatch<SetStateAction<any>>;
  lockYnComCdList: any;
  useYnComCdList: any;
  userGrantComCdList: any;
  userIdRef: React.RefObject<any>;
  userNameRef: React.RefObject<any>;
  eMailRef: React.RefObject<any>;
  isSaveClicked: boolean;
  partList: ISelectBox[];
  positionList: ISelectBox[];
  partCd: ISelectBox;
  positionCd: ISelectBox;
  setPartCd: Dispatch<SetStateAction<any>>;
  setPositionCd: Dispatch<SetStateAction<any>>;
  wWorkstatus: ISelectBox;
  setWWorkstatus: Dispatch<SetStateAction<ISelectBox>>;
  uJobdate: string;
  setUJobdate: Dispatch<SetStateAction<string>>;
  uBdate: string;
  setUBdate: Dispatch<SetStateAction<string>>;
  uRetiredate: string;
  setURetiredate: Dispatch<SetStateAction<string>>;
  userImage: string;
  setUserImage: Dispatch<SetStateAction<string>>;
  setLogoType: Dispatch<SetStateAction<any>>;
  setIsUpdatedLogo: Dispatch<SetStateAction<UpdatedLogoState>>;
}

// 조회 전용 파생 필드(사용/잔여 연차일수)용. disabled 인풋이라 실제로 호출되지 않는다.
const NOOP_SET_STATE = () => undefined;

const UserInfoInputFormCompany = ({
  style,
  state,
  setState,
  isNewAdd,
  userInfoInputFormRef,
  onClickNewAdd,
  onClickSave,
  onClickApproval,
  onClickDelete,
  onClickInitPwd,
  telNumState,
  setTelNumState,
  hpNumState,
  setHpNumState,
  ipInfoState,
  setIpInfoState,
  emerNumState,
  setEmerNumState,
  gCd,
  setGCd,
  lockYn,
  useYn,
  setUseYn,
  setLockYn,
  lockYnComCdList,
  useYnComCdList,
  userGrantComCdList,
  userIdRef,
  userNameRef,
  eMailRef,
  isSaveClicked,
  partList,
  positionList,
  partCd,
  positionCd,
  setPartCd,
  setPositionCd,
  wWorkstatus,
  setWWorkstatus,
  uJobdate,
  setUJobdate,
  uBdate,
  setUBdate,
  uRetiredate,
  setURetiredate,
  userImage,
  setUserImage,
  setLogoType,
  setIsUpdatedLogo,
}: IUserInputForm) => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const telLengthState = { num1: 3, num2: 4, num3: 4 }; // 전화번호 입력 길이
  const [hpNumLastInput, setHpNumLastInput] = useState(false); // 휴대번호 마지막인풋 길이충족 여부
  const [telNumLastInput, setTelNumLastInput] = useState(false); // 전화번호 마지막인풋 길이충족 여부
  // 잔여 연차일수 = 부여 - 사용. state의 두 값은 이미 '일' 단위로 환산되어 들어오므로 여기서 8로 나누지 않는다.
  // const leftAnnualnum = (Number(state.uAnnualnum || 0) - Number(state.uRannualnum || 0)).toString();

  // 사용/잔여 연차일수를 소수점 일수(2.5625) 대신 'N일 (M시간)' 으로 표기.
  const toAnnualText = (days: number) => {
    const hours = `${days * 8}`;
    return `${getAnnualDayString(hours)}${t('일')} (${getAnnualHourString(hours)}${t('시간')})`;
  };

  const usedAnnualnum = toAnnualText(Number(state.uRannualnum || 0));
  // 잔여 연차일수 = 부여 - 사용. 마이너스 연차 사용을 허용하므로 음수가 그대로 표시된다.
  const leftAnnualnum = toAnnualText(Number(state.uAnnualnum || 0) - Number(state.uRannualnum || 0));

  useEffect(() => {
    if (isNewAdd && state.userId !== '') setState((prev: any) => ({ ...prev, isDuplicateChecked: false }));
  }, [state.userId, isNewAdd]);

  useEffect(() => {
    setState((prev: any) => ({ ...prev, uJobdate, uAnnualnum: isOverOneYear(uJobdate) ? '15' : '12' }));
  }, [uJobdate]);

  useEffect(() => {
    setState((prev: any) => ({ ...prev, uBdate }));
  }, [uBdate]);

  useEffect(() => {
    setState((prev: any) => ({ ...prev, uRetiredate }));
  }, [uRetiredate]);

  useEffect(() => {
    setState((prev: any) => ({ ...prev, gCd: gCd[USER_GRANT], gName: gCd.cdName }));
  }, [gCd[USER_GRANT]]);

  useEffect(() => {
    if (useYn[COMCD_USE_YN] !== '') {
      setState((prev: any) => ({ ...prev, useYn: useYn[COMCD_USE_YN] }));
    }
  }, [useYn[COMCD_USE_YN]]);

  useEffect(() => {
    setState((prev: any) => ({ ...prev, partCd: partCd.PART_CD }));
  }, [partCd.PART_CD]);

  useEffect(() => {
    setState((prev: any) => ({ ...prev, positionCd: positionCd.POSITION_CD }));
  }, [positionCd.POSITION_CD]);

  useEffect(() => {
    if (lockYn[COMCD_LOCK_YN] !== '') {
      setState((prev: any) => ({ ...prev, lockYn: lockYn[COMCD_LOCK_YN] }));
    }
  }, [lockYn[COMCD_LOCK_YN]]);

  useEffect(() => {
    setState((prev: any) => ({ ...prev, uWorkstatus: wWorkstatus.wWorkstatus }));
  }, [wWorkstatus.wWorkstatus]);

  const onClickBgubun = (bgubun: '0' | '1') => {
    const updateBgubun = bgubun === '0' ? '1' : '0';
    // 0: 양력, 1: 음력
    setState((prev: any) => ({ ...prev, uBgubun: updateBgubun }));
  };

  // 현재IP버튼 API호출 후 ip setState
  const onClickGetCurrentIp = async () => {
    const res = await registerIp();
    const { data, statusCode } = res.data;

    if (statusCode === 200) {
      const splitNumber = data.split('.');
      setIpInfoState((prev: any) => ({ ...prev, num1: splitNumber[0], num2: splitNumber[1], num3: splitNumber[2], num4: splitNumber[3] }));
      setState((prev: any) => ({ ...prev, ipInfo: data }));
    } else {
      // toast.error(t(ERROR));
    }
  };

  const onClickCheckDuplicateId = async () => {
    const { isIdDuplicate, idDuplicateAlertMsg, resStatusCode } = await checkIdDuplicate(state.userId);

    if (isIdDuplicate) return toast.warning(t(idDuplicateAlertMsg));
    if (resStatusCode === 200) setState((prev: any) => ({ ...prev, isDuplicateChecked: true }));
    if (resStatusCode === 409) setState((prev: any) => ({ ...prev, userId: '' }));

    return toast.info(t(idDuplicateAlertMsg));
  };

  // 사용자 권한에 따라 버튼 렌더링
  const renderApprovalBtn = () => {
    if (auth.createAuth) {
      return (
        !isNewAdd &&
        state.approvalYn === 'N' && (
          <BtnGreen className='approvalBtn' onClick={onClickApproval}>
            {t('승인')}
          </BtnGreen>
        )
      );
    }
    return null;
  };

  const renderSaveBtn = () => {
    if (auth.createAuth || auth.updateAuth) {
      return <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>;
    }
    return null;
  };

  const renderDeleteBtn = () => {
    if (auth.deleteAuth && !isNewAdd) {
      return <BtnRed onClick={onClickDelete}>{t('삭제')}</BtnRed>;
    }
    return null;
  };

  const renderInitPwdBtn = () => {
    if (auth.createAuth || auth.updateAuth) {
      return <BtnLightBlue onClick={onClickInitPwd}>{t('비밀번호 초기화')}</BtnLightBlue>;
    }
    return null;
  };

  useEffect(() => {
    const cleanup = setupFlutterFileHandlers(
      (fileData) => {
        const base64 = fileData.base64 ?? '';
        setUserImage(base64);
        setLogoType(fileData.extension);
        setIsUpdatedLogo('U');
      },
      (error) => {
        console.error('파일 선택 오류:', error);
      }
    );
    return cleanup;
  }, []);

  return (
    <Root width={style.width}>
      <div className='inputForm' ref={userInfoInputFormRef}>
        <div className='inputForm-group-1280'>
          <div className='image-wrap col-span-img'>
            <div className='logo-input-container'>
              <LogoFileInput theme='light' useThemeLabel={false} logo={userImage} setLogo={setUserImage} setLogoType={setLogoType} isUpdatedLogo={setIsUpdatedLogo} disabled={userInfo.gCd === 'CU'} />
              <span className='description'>
                <span className='material-symbols-rounded'>info</span>
                {t('jpg,jpeg 파일 사용을 권장합니다')}
              </span>
            </div>
          </div>
          <div className='inputForm-row labelOutInput'>
            <label className='required' htmlFor='auth'>
              {t('권한')}
            </label>
            <div>
              <SelectBox
                options={userGrantComCdList}
                defaultOption={t(gCd.cdName) || t('미선택')}
                state={gCd}
                setState={setGCd}
                stateKey={USER_GRANT}
                initiateKey={gCd[USER_GRANT]}
                getBorderStyle={isSaveClicked ? applyBorderStyle(gCd[USER_GRANT], 'red', 'gCd') : undefined}
                disabled={userInfo.gCd === 'CU'}
              />
            </div>
          </div>
          <div className='inputForm-row labelInInput'>
            <Input
              className='required'
              label={t('아이디')}
              type='text'
              id='userId'
              name='userId'
              state={state}
              setState={setState}
              disabled={!isNewAdd}
              trim
              inputRef={userIdRef}
              getBorderStyle={isSaveClicked ? applyBorderStyle(state.userId, 'red', 'userId') : undefined}
              maxLength={50}
            />
            {isNewAdd && (
              <BtnGray className='btn-small' onClick={onClickCheckDuplicateId}>
                {t('중복검사')}
              </BtnGray>
            )}
          </div>
          <div className='inputForm-row labelInInput'>
            <Input
              className='required'
              label={t('이름')}
              type='text'
              id='userName'
              name='userName'
              state={state}
              setState={setState}
              inputRef={userNameRef}
              getBorderStyle={isSaveClicked ? applyBorderStyle(state.userName, 'red', 'userName') : undefined}
              maxLength={25}
              disabled={userInfo.gCd === 'CU'}
            />
          </div>
          <div className='inputForm-row labelInInput'>
            <Input
              className='required'
              label={t('이메일')}
              type='text'
              id='eMail'
              name='eMail'
              state={state}
              setState={setState}
              trim
              inputRef={eMailRef}
              getBorderStyle={isSaveClicked ? applyBorderStyle(state.eMail, 'red', 'eMail') : undefined}
              maxLength={50}
              disabled={userInfo.gCd === 'CU'}
            />
          </div>
          {!isNewAdd && userInfo.gCd !== 'CU' && (
            <div className='inputForm-row labelOutInput'>
              <label htmlFor='password'>{t('비밀번호')}</label>
              {renderInitPwdBtn()}
              {!isNewAdd && userInfo.gCd === 'MM' && <div className='viewOnly'>{state.userPwd && `(${state.userPwd})`}</div>}
            </div>
          )}
        </div>
        <div className='inputForm-group-1280'>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='hpNum' className='required'>
              {t('휴대폰번호')}
            </label>
            <NumbersInput
              state={hpNumState}
              setState={setHpNumState}
              lengthState={telLengthState}
              setIsLastInputCompleted={setHpNumLastInput}
              isSaveClicked={isSaveClicked}
              disabled={userInfo.gCd === 'CU'}
            />
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='telNum'>{t('전화번호')}</label>
            <NumbersInput
              state={telNumState}
              setState={setTelNumState}
              lengthState={telLengthState}
              setIsLastInputCompleted={setTelNumLastInput}
              isFirstInputFocused={hpNumLastInput}
              disabled={userInfo.gCd === 'CU'}
            />
          </div>
        </div>
        <div className='inputForm-group-1280'>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='emerNum'>{t('비상 연락처')}</label>
            <NumbersInput state={emerNumState} setState={setEmerNumState} lengthState={telLengthState} isFirstInputFocused={telNumLastInput} disabled={userInfo.gCd === 'CU'} />
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='wWorkstatu required'>{t('근로 상태')}</label>
            <SelectBox
              options={WORK_STATUS_SEARCH_WITHOUT_ALL}
              defaultOption={t('재직')}
              state={wWorkstatus}
              setState={setWWorkstatus}
              stateKey='wWorkstatus'
              initiateKey={wWorkstatus.wWorkstatus}
              disabled={userInfo.gCd === 'CU'}
            />
          </div>
        </div>
        {userInfo.gCd !== 'CU' && (
          <>
            <div className='inputForm-group-1536 withBtn'>
              <div className='inputForm-row labelOutInput content-overflow'>
                <label htmlFor={t('주소')}>{t('우편번호')}</label>
                <Input type='number' id='postNum' name='postNum' state={state} setState={setState} placeholder={t('우편번호')} disabled={userInfo.gCd === 'CU'} />
              </div>
              <div className='inputForm-row labelOutInput'>
                <AddressSearch setState={setState} disabled={userInfo.gCd === 'CU'} />
              </div>
            </div>
            <div className='inputForm-group-1536'>
              <div className='inputForm-row labelOutInput'>
                <label htmlFor={t('주소')}>{t('주소')}</label>
                <Input type='address' id='address1' name='address1' state={state} setState={setState} placeholder={t('주소')} disabled={userInfo.gCd === 'CU'} />
              </div>
              <div className='inputForm-row labelInInput'>
                <Input label='상세주소' type='text' id='address2' name='address2' state={state} setState={setState} placeholder={t('상세주소')} maxLength={25} disabled={userInfo.gCd === 'CU'} />
              </div>
            </div>
          </>
        )}
        <div className='inputForm-group-1536'>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='sDate'>{t('입사일자')}</label>
            <div className='datePickerWrapper'>
              <DatePickerComponent startDate={uJobdate} setDate={setUJobdate} popperPlacement='bottom' disabled={userInfo.gCd === 'CU'} />
            </div>
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='fDate'>{t('퇴사일자')}</label>
            <div className='datePickerWrapper'>
              <DatePickerComponent startDate={uRetiredate} setDate={setURetiredate} popperPlacement='bottom' disabled={userInfo.gCd === 'CU'} />
            </div>
          </div>
        </div>
        <div className='inputForm-group-1280'>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='loginDate' className='required'>
              {t('부서명')}
            </label>
            <SelectBox
              options={partList}
              defaultOption={partCd.cdName || t('미선택')}
              state={partCd}
              setState={setPartCd}
              stateKey='PART_CD'
              initiateKey={partCd.PART_CD}
              getBorderStyle={isSaveClicked ? applyBorderStyle(partCd.PART_CD, 'red', 'partCd') : undefined}
              disabled={userInfo.gCd === 'CU'}
            />
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='loginDate' className='required'>
              {t('직급')}
            </label>
            <SelectBox
              options={positionList}
              defaultOption={positionCd.cdName || t('미선택')}
              state={positionCd}
              setState={setPositionCd}
              stateKey='POSITION_CD'
              initiateKey={positionCd.POSITION_CD}
              getBorderStyle={isSaveClicked ? applyBorderStyle(positionCd.POSITION_CD, 'red', 'positionCd') : undefined}
              disabled={userInfo.gCd === 'CU'}
            />
          </div>
        </div>
        {/* 연차 정보: 부여(입력) - 사용(서버 관리) - 잔여(파생) 순. 기존 2열 그리드를 따라 흐른다 */}
        <div className='inputForm-group-1280'>
          <div className='inputForm-row labelInInput'>
            <Input label={t('연차일수')} type='number' id='uAnnualnum' name='uAnnualnum' state={state} setState={setState} maxLength={2} disabled={userInfo.gCd === 'CU'} />
          </div>
          {/* 사용 연차일수는 근태 결재 완료 시 서버가 갱신하므로 조회 전용. 표시 문자열만 넘긴다 */}
          <div className='inputForm-row labelInInput'>
            <Input label={t('사용 연차일수')} type='text' id='usedAnnualnum' name='usedAnnualnum' state={{ usedAnnualnum }} setState={NOOP_SET_STATE} disabled />
          </div>
          {/* 잔여 연차일수는 부여-사용에서 파생되는 값이라 state에 담지 않고 표시용으로만 계산 */}
          <div className='inputForm-row labelInInput'>
            <Input label={t('잔여 연차일수')} type='text' id='leftAnnualnum' name='leftAnnualnum' state={{ leftAnnualnum }} setState={NOOP_SET_STATE} disabled />
          </div>
          <div className='inputForm-row labelOutInput'>
            <label htmlFor='uBdate'>{t('생년월일')}</label>
            <div className='flex-basic gap50'>
              <div className='datePickerWrapper'>
                <DatePickerComponent startDate={uBdate} setDate={setUBdate} popperPlacement='bottom' disabled={userInfo.gCd === 'CU'} />
              </div>
              <div className='flex-basic gap50'>
                <label htmlFor='uBgubun' style={{ width: 'fit-content' }}>
                  {t('양력')}
                </label>
                <ClickableContainer className='flex-start' button={<TableLamp use={state.uBgubun === '0'} />} onClick={() => onClickBgubun(state.uBgubun)} disabled={userInfo.gCd === 'CU'} />
              </div>
            </div>
          </div>
        </div>
        {userInfo.gCd !== 'CU' && (
          <>
            <div className='inputForm-group-1536 withBtn inputColumnFour'>
              <div className='inputForm-row labelOutInput'>
                <label htmlFor='hpNum'>{t('접속 IP')}</label>
                <IpInput stateName='ipInfo' separator='.' state={ipInfoState} setState={setIpInfoState} setInfoState={setState} disabled={userInfo.gCd === 'CU'} />
              </div>
              <div className='inputForm-row labelOutInput'>
                <BtnGray className='btn-small' onClick={onClickGetCurrentIp}>
                  {t('현재 PC IP 적용')}
                </BtnGray>
              </div>
            </div>
            <div className='inputForm-group-1280'>
              <div className='inputForm-row labelOutInput'>
                <label htmlFor='lockYn'>{t('잠금유무')}</label>
                <SelectBox
                  options={lockYnComCdList}
                  defaultOption={isNewAdd ? t('미잠금') : t(state.lockYn)}
                  state={lockYn}
                  setState={setLockYn}
                  stateKey={COMCD_LOCK_YN}
                  initiateKey={lockYn[COMCD_LOCK_YN]}
                />
              </div>
              <div className='inputForm-row labelOutInput'>
                <label htmlFor='useYn'>{t('사용유무')}</label>
                <SelectBox options={useYnComCdList} defaultOption={isNewAdd ? t('사용') : t(state.useYn)} state={useYn} setState={setUseYn} stateKey={COMCD_USE_YN} initiateKey={useYn[COMCD_USE_YN]} />
              </div>
              {!isNewAdd && (
                <div className='inputForm-row labelOutInput'>
                  <label htmlFor='useYn'>{t('승인여부')}</label>
                  <div className='viewOnly'>{state.approvalYn === 'Y' ? t('승인') : t('미승인')}</div>
                </div>
              )}
              <div className='inputForm-row labelInInput'>
                <Input label={t('비고')} type='text' id='bigo' name='bigo' state={state} setState={setState} maxLength={100} />
              </div>
            </div>
          </>
        )}

        {!isNewAdd && userInfo.gCd !== 'CU' && (
          <div className='detailInfo-group'>
            <div className='flex-col detailInfo'>
              <label htmlFor='loginDate'>{t('로그인 일시')}</label>
              <div>{state.loginDate}</div>
            </div>
            <div className='flex-col detailInfo'>
              <label htmlFor='loginerrorCount'>{t('로그인 에러 횟수')}</label>
              <div>{state.loginerrorCount}</div>
            </div>

            <div className='flex-col detailInfo'>
              <label htmlFor='pwdChangeDate'>{t('비밀번호 변경일')}</label>
              <div>{formatDate(state.pwdChangeDate)}</div>
            </div>
            <div className='flex-col detailInfo'>
              <label htmlFor='pwdCheckYn'>{t('재입력 유무')}</label>
              <div>{state.pwdCheckYn === 'Y' ? t('적용') : t('미적용')}</div>
            </div>

            <div className='flex-col detailInfo'>
              <label htmlFor='ip'>{t('최종접속 IP')}</label>
              <div>{state.ip}</div>
            </div>

            <div className='flex-col detailInfo'>
              <label htmlFor='createDate'>{t('등록일자')}</label>
              <div>{state?.wDate}</div>
            </div>
            <div className='flex-col detailInfo'>
              <label htmlFor='createUser'>{t('등록자')}</label>
              <div>{state.writer}</div>
            </div>

            <div className='flex-col detailInfo'>
              <label htmlFor='updateDate'>{t('수정일자')}</label>
              <div>{state.eDate}</div>
            </div>
            <div className='flex-col detailInfo'>
              <label htmlFor='updateUser'>{t('수정자')}</label>
              <div>{state.editor}</div>
            </div>
          </div>
        )}
      </div>
      {userInfo.gCd !== 'CU' && (
        <ButtonsWrapper>
          {renderDeleteBtn()}
          {renderApprovalBtn()}
          {renderSaveBtn()}
        </ButtonsWrapper>
      )}
    </Root>
  );
};

export default React.memo(UserInfoInputFormCompany);
